/**
 * F-10 viewport windowing — which rows the table actually mounts.
 *
 * PURE board-unit math (no DOM, no React), so it is unit-testable and shared
 * between the grid's render and any future consumer.
 *
 * The decision (docs/10 §3, measured 2026-07-16):
 *
 *  - **Rows only.** Columns are few (F-10: new boards are 2×3; the translation
 *    workload is Original Script + targets) and the CSS grid needs every track
 *    anyway — column windowing buys nothing at this shape.
 *  - **`first`/`last` by binary search over the predicted edge array** — O(log n)
 *    per pan frame, not O(n). At 50k rows the linear scan alone would be the
 *    per-frame budget.
 *  - **Buffer ≈ one viewport height** above and below. Measured re-render of the
 *    mounted range is 0.6–0.8 ms, so the buffer is nearly free, and 900 board px
 *    covers ~375 ms of a 2 400 px/s pan.
 *  - **NO scrollbars, and windowing is NOT for pan** (F-121: the table never
 *    scrolls internally — you pan the board). Pan was already at the compositor
 *    baseline at every table size, because the camera is one CSS transform on a
 *    promoted layer. Windowing exists for EDIT LATENCY (73 ms → 0.6 ms per cell
 *    at 10k), ZOOM (60 ms → 12 ms/frame) and MOUNT (680 ms → 83 ms).
 *
 * The camera itself is untouched (docs/04 §5): the wrapper keeps the table's
 * FULL extent so the camera pans over the whole thing, and the mounted block is
 * absolutely positioned at its predicted edge.
 */
import type { Camera } from "@kola/shared";

import type { ViewportSize } from "./canvas.js";

/**
 * A half-open row range to mount: rows `first..last` INCLUSIVE.
 * `last < first` means "nothing intersects the view" — a legitimate state
 * (the board is panned off the table entirely), not an error.
 */
export interface RowWindow {
  first: number;
  last: number;
}

/** No row intersects the view. */
export const EMPTY_ROW_WINDOW: Readonly<RowWindow> = Object.freeze({ first: 0, last: -1 });

export function isEmptyRowWindow(win: RowWindow): boolean {
  return win.last < win.first;
}

export function rowWindowsEqual(a: RowWindow, b: RowWindow): boolean {
  // Every empty window is the same window: the render mounts nothing either
  // way, so treating (0,-1) and (5,4) as different would re-render for free.
  if (isEmptyRowWindow(a) && isEmptyRowWindow(b)) return true;
  return a.first === b.first && a.last === b.last;
}

/**
 * The visible board rect a camera sees, in BOARD units.
 *
 * The camera layer renders `translate(-x·zoom, -y·zoom) scale(zoom)`, so the
 * board point at the viewport's top-left is exactly `(camera.x, camera.y)` and
 * the viewport spans `viewport / zoom` board units.
 */
export function visibleBoardRect(
  camera: Camera,
  viewport: ViewportSize,
): { top: number; bottom: number; left: number; right: number } {
  const w = viewport.width / camera.zoom;
  const h = viewport.height / camera.zoom;
  return { top: camera.y, bottom: camera.y + h, left: camera.x, right: camera.x + w };
}

/**
 * First index in `edges` whose interval END is strictly past `pos` — i.e. the
 * first row not entirely above `pos`. Returns `rowCount` when every row ends
 * at or before `pos`. `edges` is the cumulative edge array (length rowCount+1,
 * monotonically non-decreasing), so this is a plain lower-bound search.
 */
function firstRowEndingAfter(edges: readonly number[], pos: number): number {
  let lo = 0;
  let hi = edges.length - 1; // = rowCount
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    // edges[mid + 1] is row `mid`'s bottom edge.
    if ((edges[mid + 1] ?? Number.POSITIVE_INFINITY) <= pos) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Last index in `edges` whose interval START is strictly before `pos` — i.e.
 * the last row that begins above `pos`. Returns -1 when every row starts at or
 * after `pos`.
 */
function lastRowStartingBefore(edges: readonly number[], pos: number): number {
  let lo = 0;
  let hi = edges.length - 1; // one past the last row index
  // Find the first row whose TOP is >= pos; the one before it is the answer.
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((edges[mid] ?? Number.NEGATIVE_INFINITY) < pos) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

/**
 * The rows intersecting the band `[top, bottom)` of a table whose row edges are
 * `edges` (wrapper-relative board units, `edges[r]` = row r's top edge,
 * `edges[rowCount]` = the table's bottom content edge).
 *
 * The caller applies the buffer by widening `top`/`bottom` — see
 * `bufferedRowWindow`. Intersection is treated as OVERLAP: a row is in the
 * window if any part of it is inside the band. Both bounds are found by binary
 * search (docs/10 §3), so this is O(log n) per pan frame at any table size.
 */
export function rowWindow(edges: readonly number[], top: number, bottom: number): RowWindow {
  const rowCount = edges.length - 1;
  if (rowCount <= 0) return { ...EMPTY_ROW_WINDOW };
  if (bottom <= top) return { ...EMPTY_ROW_WINDOW };
  const first = firstRowEndingAfter(edges, top);
  const last = lastRowStartingBefore(edges, bottom);
  // Off the top or the bottom of the table entirely.
  if (first >= rowCount || last < 0 || last < first) return { ...EMPTY_ROW_WINDOW };
  return { first, last };
}

/**
 * The mounted row range for a camera: the rows intersecting the visible board
 * rect, plus ONE VIEWPORT HEIGHT of buffer above and below (docs/10 §3).
 *
 * The buffer is the visible height itself rather than a constant 900 px: at
 * zoom 0.25 a 900 px viewport sees 3 600 board px, and a fixed 900 px buffer
 * would be a quarter of a screen — enough to make rows visibly pop in during a
 * fast zoomed-out pan. Deriving it from the viewport keeps "one viewport" true
 * at every zoom, which is what was measured.
 *
 * `originY` is the table wrapper's board-space Y (GRID_ORIGIN.y): `edges` are
 * wrapper-relative, the camera is in board space.
 */
export function bufferedRowWindow(
  edges: readonly number[],
  camera: Camera,
  viewport: ViewportSize,
  originY: number,
): RowWindow {
  const view = visibleBoardRect(camera, viewport);
  const buffer = view.bottom - view.top;
  return rowWindow(edges, view.top - originY - buffer, view.bottom - originY + buffer);
}

/* ------------------------------------------------------------------ */
/* Culling rect (F-10: elements/pins on off-screen rows)               */
/* ------------------------------------------------------------------ */

/** A board-space rect, buffered for culling. */
export interface CullRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The visible board rect grown by one viewport in every direction — what the
 * elements layer mounts.
 *
 * Windowing the table's rows is only half of F-10 at scale: a contained
 * element (F-36) resolves its position from the grid's published edges, which
 * now cover EVERY row, so without a cull an element anchored to row 9 000
 * would happily mount (and load its image) while its cell is nowhere near the
 * screen. The buffer is the same "one viewport" as the row window, for the
 * same measured reason.
 */
export function cullRect(camera: Camera, viewport: ViewportSize): CullRect {
  const view = visibleBoardRect(camera, viewport);
  const bx = view.right - view.left;
  const by = view.bottom - view.top;
  return {
    left: view.left - bx,
    right: view.right + bx,
    top: view.top - by,
    bottom: view.bottom + by,
  };
}

/** True when any part of `rect` lies inside `cull`. */
export function rectIntersectsCull(
  rect: { x: number; y: number; w: number; h: number },
  cull: CullRect,
): boolean {
  return (
    rect.x + rect.w >= cull.left &&
    rect.x <= cull.right &&
    rect.y + rect.h >= cull.top &&
    rect.y <= cull.bottom
  );
}

/** True when `inner` is entirely inside `outer` (used to hold a cull rect). */
export function cullRectContains(outer: CullRect, inner: CullRect): boolean {
  return (
    inner.left >= outer.left &&
    inner.right <= outer.right &&
    inner.top >= outer.top &&
    inner.bottom <= outer.bottom
  );
}
